import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    forwardRef,
    HostBinding,
    inject,
    Input,
    input,
    model,
    output,
    signal,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    NG_VALUE_ACCESSOR,
    ControlContainer,
    FormGroupDirective,
    AbstractControl,
    ValidationErrors,
    Validator,
    ControlValueAccessor,
    NG_VALIDATORS
} from '@angular/forms';

import { Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import {
    QueryFilterGroup,
    FetchQuerySortInfo,
    ObjectId
} from 'zigra-ng-framework';

import { InputTextModule } from 'primeng/inputtext';
import { OverlayModule } from 'primeng/overlay';
import { ZTableVSSelectComponent } from '../../form-components/z-table-select/z-table-virtual-scroller-select.component';

import { NoAction } from '../../../zigra-schemas/custom/no-action';

import { DialogModule } from 'primeng/dialog';
import {
    IDataTableSelectedRow,
    DataTableSchema,
    DataTableFactory,
    DataTableColumn
} from '@shared/components/form-components/helpers/data-table-schema';
import { EntityFormModeType } from '@shared/types/entity-form-mode.type';
import {
    CustomerType,
    CustomerGraphQLSchemaActions,
    CustomerTypeQuery,
    AddressTypeQuery,
    CityTypeQuery,
    FirstNameTypeQuery,
    InfoNoteTypeQuery,
    OccupationActivityTypeQuery,
    ContactDetailTypeQuery,
    FinancialDetailTypeQuery,
    TraderProfitMarginTypeQuery,
    CustomerGraphQLSchemaSelectors,
    TAXOfficeTypeQuery,
    CustomersToEntitiesSubjectTypeQuery,
    EntitySubjectInfoTypeQuery
} from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import { ContactPrimaryPhonePipe } from '../../../pipes/contacts-pipes/contact-primary-phone.pipe';
import { CustomerPrimaryContactAddressPipe } from '../../../pipes/contacts-pipes/customer-primary-contact-address.pipe';
import { CustomerlegalTypePipe } from '../../../pipes/contacts-pipes/customer-legal-type.pipe';
import { CustomerSubjectsPipe } from '../../../pipes/contacts-pipes/customer-subjects.pipe';
import { ButtonModule } from 'primeng/button';
import { ClientCardOptions } from '@portal/clients/entities/client-card.options';
import { ZClientCardFooterComponent } from '@portal/clients/z-client-card-footer/z-client-card-footer.component';
import { ZClientCardComponent } from '@portal/clients/z-client-card/z-client-card.component';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ZionMessagesService } from '@shared/services/ax-messages.service';
import { ToastMessageOptions } from 'primeng/api';

@Component({
    selector: 'z-customer-select',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        DialogModule,
        OverlayModule,
        TableModule,
        InputTextModule,
        ZTableVSSelectComponent,
        ContactPrimaryPhonePipe,
        CustomerPrimaryContactAddressPipe,
        CustomerlegalTypePipe,
        CustomerSubjectsPipe,
        ButtonModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ZCustomerSelectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: ZCustomerSelectComponent
        }
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    templateUrl: `./z-customer-select.component.html`,
    styleUrl: './z-customer-select.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class ZCustomerSelectComponent
    implements ControlValueAccessor, Validator
{
    @HostBinding('class') class?: string = 'flex flex-auto align-self-center1';
    @HostBinding('style') style?: string;
    @Input() index?: number;
    mode = model<'edit' | 'view' | 'lockedit'>('view');
    label = input<string | undefined>('Είδος');
    autocomplete = input<string | undefined>(undefined);
    id = input<string | undefined>(undefined);
    optionLabel = input<string | undefined>('Title');
    optionValue = input<string | undefined>('_id');
    inputDisable = model<boolean>(false);
    fetchSubjects = model<boolean>(false);
    searchable = model<boolean>(true);
    showEditCustomerIcon = model<boolean>(true);
    showNewCustomerIcon = model<boolean>(true);
    showClearIcon = model<boolean>(true);

    options = input<CustomerType[] | undefined>(undefined);
    protected _options = signal<CustomerType[] | undefined>(undefined);
    subjectId = input<ObjectId | undefined | null>(undefined);

    rowDataSelect = output<{
        customerType: CustomerType | undefined;
        rowIndex?: number;
    }>();
    inputFocus = output<number>();
    inputBlur = output<number>();
    dblclick = output();
    // newIconButton = output();
    // editIconButton = output<any | undefined>();

    selectedCustomer = input<CustomerType | undefined>();

    fetchDataForEditById = signal<
        { formModeType: EntityFormModeType; customerId?: ObjectId } | undefined
    >(undefined);

    selectedOption = signal<IDataTableSelectedRow<CustomerType> | undefined>(
        undefined
    );
    selectedOptionContactPhone = signal<string | undefined>(undefined);
    placeholder = input<string | undefined>(undefined);
    store = inject(Store);
    messageService = inject(ZionMessagesService);
    dialogService = inject(DialogService);

    protected customerVisibleDialog: boolean = false;
    protected dataTableSchema = signal<DataTableSchema | undefined>(undefined);
    protected totalRecords = signal<number>(0);
    private _searchTerm?: string;

    constructor(private cd: ChangeDetectorRef) {
        this.dataTableSchema.set(
            DataTableFactory.AddDatatableSchema([
                DataTableColumn.StringColumn('SureName', 'Επωνυμία', {
                    searchable: true
                }),
                DataTableColumn.StringColumn('FirstName.Name', 'Όνομα', {
                    searchable: true
                }),
                DataTableColumn.StringColumn('Code', 'Κωδικός', {
                    searchable: true
                }),
                DataTableColumn.StringColumn('Addresses[0].City.Title', 'Πόλη'),
                DataTableColumn.StringColumn(
                    'Addresses[0].StreetName',
                    'Διεύθυνση'
                ),
                DataTableColumn.StringColumn(
                    'Addresses[0].StreetNumber',
                    'Αριθμός'
                ),
                DataTableColumn.StringColumn('Addresses[0].PostalCode', 'Τ.Κ')
            ])
        );

        effect(() => {
            this.model = this.selectedCustomer()?._id;

            this.onModelChange(this.selectedCustomer()?._id);

            this.selectedOption.set({
                data: this.selectedCustomer(),
                index: 0
            } as IDataTableSelectedRow);

            this.selectedOptionContactPhone.set(
                new ContactPrimaryPhonePipe().transform(this.selectedCustomer())
            );
        });

        effect(() => {
            const selectedOption = this.selectedOption();
            this.selectedOptionContactPhone.set(
                new ContactPrimaryPhonePipe().transform(selectedOption?.data)
            );
        });

        // this.store
        //   .select(CustomerGraphQLSchemaSelectors.FetchAndCountSelector('', ''))
        //   .pipe(takeUntilDestroyed())
        //   .subscribe((fetchResult) => {
        //     console.debug(
        //       'CustomerGraphQLSchemaSelectors.FetchAndCountSelector ',
        //       fetchResult?.data.FetchAndCount,
        //     );
        //     this.options.set(
        //       Array.from({ length: fetchResult?.data.FetchAndCount ?? 0 }),
        //     );
        //     this.totalRecords.set(fetchResult?.data.FetchAndCount ?? 0);
        //   });

        // effect(() => {
        //     this.selectedOption.set({
        //         data: undefined,
        //         index: 0
        //     });
        //     if (this.selectedCustomer() != undefined)
        //         this.fetchDataForEditById.set({
        //             formModeType: 'view',
        //             customerId: this.selectedCustomer()?._id
        //         });
        //     this.selectedOption.set({
        //         data: this.selectedCustomer(),
        //         index: 0
        //     });
        // });
    }

    /* NG_VALUE_ACCESSOR Implementation */
    // https://blog.angular-university.io/angular-custom-form-controls/
    disabled: boolean = false;
    model: any;
    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    writeValue(customerId: ObjectId): void {
        this.model = customerId;
        this.loadOption();
        //if (this.model == undefined) this.selectedOption.set(undefined);

        // if (this.model == undefined) return;

        // this.store
        //   .dispatch(
        //     new CustomerGraphQLSchemaActions.Fetch(
        //       {
        //         ...CustomerTypeQuery.GQLQuery(),
        //         Addresses: AddressTypeQuery.GQLQuery({
        //           City: CityTypeQuery.GQLQuery(),
        //         }),
        //         FirstName: FirstNameTypeQuery.GQLQuery(),
        //         FatherName: FirstNameTypeQuery.GQLQuery(),
        //         InfoNotes: InfoNoteTypeQuery.GQLQuery(),
        //         OccupationActivity: OccupationActivityTypeQuery.GQLQuery(),
        //         ContactDetails: ContactDetailTypeQuery.GQLQuery(),
        //         // Courier: CourierTypeQuery.GQLQuery(),
        //         FinancialDetails: FinancialDetailTypeQuery.GQLQuery(),
        //         TraderProfitMargin: TraderProfitMarginTypeQuery.GQLQuery(),
        //       },
        //       {
        //         GroupQuery: QueryFilterGroup.FieldIsEqualToObjectId(
        //           ['_id'],
        //           this.model,
        //         ),
        //       },
        //     ),
        //   )
        //   .subscribe(() => {
        //     const fetchResult = this.store.selectSnapshot(
        //       CustomerGraphQLSchemaSelectors.FetchSelector('', ''),
        //     );
        //     this.selectedOption.update((option) => {
        //       return {
        //         data: fetchResult?.data?.Fetch?.find(
        //           (item) => item._id == this.model,
        //         ),
        //       } as IDataTableSelectedRow<CustomerType>;
        //     });

        //     this.onModelChange(this.model);
        //   });
    }

    registerOnChange(fn: any): void {
        this.onModelChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onModelTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.inputDisable.set(isDisabled);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return null;
    }
    registerOnValidatorChange?(fn: () => void): void {
        // throw new Error('Method not implemented.');
    }
    /* /NG_VALUE_ACCESSOR Implementation */

    ngOnInit() {
        // Initialize empty array of Options
        //this.options.set(Array.from({ length: 120 }));
        //this.totalRecords.set(120);

        // On Init fetch the count of all to create the default options array
        this.searchTermChange();
    }

    // Εδώ έρχεται όταν αλλάξουν τα Options.
    // Αν ταυτόχρονα υπάρχει και model τότε επιλέγουμε την εγγραφή από τα options που λέει το model.
    ngOnChanges(simpleChange: SimpleChanges) {
        if (simpleChange['options']?.currentValue != undefined) {
            const option = this.options()?.find(
                (customer) => customer._id == this.model
            );

            if (option) {
                this.selectedOption.set({
                    index: 0,
                    data: option,
                    searchterm: undefined
                } as IDataTableSelectedRow);
            }
        }
    }

    onNgModelChange(event: any) {
        this.onModelChange(event);
    }

    loadOption() {
        if (this.model == undefined) {
            this.selectedOption.set(undefined);
            return;
        }

        const groupOfIds =
            QueryFilterGroup.FieldIsEqualToObjectIds<CustomerType>(
                ['_id'],
                [this.model]
            );

        this.store
            .dispatch(
                new CustomerGraphQLSchemaActions.Fetch(
                    this.fetchSubjects()
                        ? customersQueryForBatchWithSubjects
                        : customersQueryForBatch,
                    {
                        GroupQuery: groupOfIds //this.getGroupQuery(),
                    }
                )
            )
            .subscribe(() => {
                this.selectedOption.set({
                    index: 0,
                    data: (this.store.selectSnapshot(
                        CustomerGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data.Fetch ?? [])?.[0],
                    searchterm: undefined
                } as IDataTableSelectedRow);
            });
    }

    loadData(event: TableLazyLoadEvent) {
        debugger;
        // Αν υπάρχουν options και υπάρχει και επιλεγμένο πελάτης τότε θα ζητάμε μόνο τα οχημάτα που έχουν τα ids των options (τα Options αυτά είναι του πελάτη).
        const selectedOptions =
            this.options()?.map((vehicle) => vehicle._id ?? '') ?? [];

        const groupQuery = this.getGroupQuery();

        const groupOfIds =
            QueryFilterGroup.FieldIsEqualToObjectIds<CustomerType>(
                ['_id'],
                selectedOptions
            );

        if (this.subjectId() != undefined && selectedOptions.length == 0) {
            // Αν έχει επιλεγεί πελάτης και δεν υπάρχουν οχήματα δεμένα πάνω του, τότε δεν δείχνουμε τίποτα
            this.totalRecords.set(0);
            this._options.set([]);
            return;
        }

        const group =
            this.subjectId() != undefined
                ? QueryFilterGroup.JoinWithAndOperator([groupQuery, groupOfIds])
                : groupQuery;

        console.debug('this.fetchSubjects()', this.fetchSubjects());
        debugger;

        this.store
            .dispatch([
                this.totalRecords() == 0
                    ? new CustomerGraphQLSchemaActions.FetchAndCount(
                          this.fetchSubjects()
                              ? customersQueryForBatchWithSubjects
                              : customersQueryForBatch,
                          {
                              GroupQuery: group
                          }
                          //QueryFilter.FetchQueryParamsFromAGGridParams({}),
                      )
                    : new NoAction(),
                new CustomerGraphQLSchemaActions.Fetch(
                    this.fetchSubjects()
                        ? customersQueryForBatchWithSubjects
                        : customersQueryForBatch,
                    {
                        GroupQuery: group, //this.getGroupQuery(),
                        SortQuery: [
                            {
                                Identifier: 'SureName',
                                Order: 'ASC'
                            } as FetchQuerySortInfo
                        ],
                        Skip: event.first,
                        Limit: event.rows ?? 100
                    }
                )
            ])
            .subscribe(() => {
                if (this.totalRecords() == 0) {
                    const fetchAndCount = this.store.selectSnapshot(
                        CustomerGraphQLSchemaSelectors.FetchAndCountSelector(
                            '',
                            ''
                        )
                    );

                    // this.options.set(
                    //     Array.from({
                    //         length: fetchAndCount?.data.FetchAndCount ?? 0
                    //     })
                    // );

                    this.totalRecords.set(
                        fetchAndCount?.data.FetchAndCount ?? 0
                    );
                }

                ///
                const first = event.first ?? 0;
                const rows = event.rows ?? 0;
                const fetchData =
                    this.store.selectSnapshot(
                        CustomerGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data.Fetch ?? [];

                this._options.set(fetchData);
                // this.options.update((options) => {
                //     const fetchData =
                //         this.store.selectSnapshot(
                //             CustomerGraphQLSchemaSelectors.FetchSelector('', '')
                //         )?.data.Fetch ?? [];
                //     Array.prototype.splice.apply(options, [
                //         first,
                //         rows,
                //         ...fetchData
                //     ]);

                //     return options;
                // });
                event.forceUpdate?.();
            });
    }

    private getGroupQuery() {
        const searchTokens = this._searchTerm?.split(' ');
        const conditions = searchTokens?.map((token) => {
            return QueryFilterGroup.JoinWithOrOperator([
                QueryFilterGroup.FieldContainsString<CustomerType>(
                    ['SureName'],
                    token?.trim()
                ),
                QueryFilterGroup.FieldContainsString<CustomerType>(
                    ['Code'],
                    token?.trim()
                )
            ]);
        });
        const group = QueryFilterGroup.JoinWithAndOperator(conditions ?? []);
        return group;
    }

    searchTermChange(searchTerm?: string) {
        this._searchTerm = searchTerm;
        this.totalRecords.set(0);

        // Fetch the total rows for the requested term. On Select reset the options Array.
        // this.store.dispatch(
        //   new CustomerGraphQLSchemaActions.FetchAndCount(
        //     CustomerTypeQuery.GQLQuery(),
        //     {
        //       GroupQuery: this.getGroupQuery(),
        //     },
        //     //QueryFilter.FetchQueryParamsFromAGGridParams({}),
        //   ),
        // );
    }

    onLazyLoad($event: TableLazyLoadEvent) {
        this.loadData($event);
    }

    onDblClick() {
        //this.dblclick.emit();
    }
    onRowDataSelect(selectedRow?: IDataTableSelectedRow<CustomerType>) {
        if (this.mode() != 'lockedit') this.mode.set('view');

        this.model = selectedRow?.data?._id;

        this.selectedOption.set(selectedRow);

        debugger;
        this.onModelChange(selectedRow?.data?._id);

        //if (selectedRow?.data)
        this.rowDataSelect.emit({
            customerType: selectedRow?.data,
            rowIndex: this.index
        });
    }

    onNewIconButton() {
        //this.newIconButton.emit();
        this.newCustomer();
    }

    onEditIconButton(data: any | undefined) {
        //this.editIconButton.emit(data);
        this.editCustomer(data);
    }

    onInputFocus() {
        if (this.index != undefined) this.inputFocus.emit(this.index);
    }
    onInputBlur() {
        if (this.index != undefined) this.inputBlur.emit(this.index);
    }

    onInputDblClick() {
        this.customerVisibleDialog = true;
    }

    editCustomer(customer: CustomerType) {
        const dialogRef = this.dialogService.open<ZClientCardComponent>(
            ZClientCardComponent,
            {
                header: 'Μεταβολή Καρτέλας Πελάτη',
                height: '700px',
                width: '1100px',
                modal: true,
                resizable: true,
                draggable: true,
                closable: true,
                maximizable: false,
                closeOnEscape: false,
                focusOnShow: false,
                templates: {
                    footer: ZClientCardFooterComponent
                },
                data: {
                    customerId: customer?._id, // this.selectedCustomer()?._id,
                    mode: 'edit',
                    onSave: (options, customer) => {
                        debugger;
                        this.messageService.add({
                            summary: 'Μεταβολή Καρτέλας Πελάτη',
                            detail: 'Ο Πελάτης καταχωρήθηκε επιτυχώς',
                            severity: 'success',
                            key: 'br'
                        } as ToastMessageOptions);
                        this.selectedOption.set({
                            data: customer,
                            index: 0
                        } as IDataTableSelectedRow);

                        this.onRowDataSelect({
                            data: customer,
                            index: 0
                        });
                        dialogRef.close();
                    }
                } as ClientCardOptions
            } as DynamicDialogConfig<ClientCardOptions>
        );
    }

    newCustomer() {
        const dialogRef = this.dialogService.open<ZClientCardComponent>(
            ZClientCardComponent,
            {
                header: 'Νέα Καρτέλα Πελάτη',
                height: '700px',
                width: '1100px',
                modal: true,
                resizable: true,
                draggable: true,
                closable: true,
                maximizable: false,
                closeOnEscape: false,
                focusOnShow: false,
                templates: {
                    footer: ZClientCardFooterComponent
                },
                data: {
                    customerId: undefined,
                    mode: 'add',
                    onSave: (options, customer) => {
                        this.messageService.add({
                            summary: 'Νέα Καρτέλα Πελάτη',
                            detail: 'Ο Πελάτης καταχωρήθηκε επιτυχώς',
                            severity: 'success',
                            key: 'br'
                        } as ToastMessageOptions);

                        this.selectedOption.set({
                            data: customer,
                            index: 0
                        } as IDataTableSelectedRow);

                        this.onRowDataSelect({
                            data: customer,
                            index: 0
                        });
                        dialogRef.close();
                    }
                } as ClientCardOptions
            } as DynamicDialogConfig<ClientCardOptions>
        );
    }
}

const customersQueryForBatchWithSubjects = CustomerTypeQuery.GQLQuery({
    ...CustomerTypeQuery.GQLQuery(),
    Addresses: AddressTypeQuery.GQLQuery({
        City: CityTypeQuery.GQLQuery()
    }),
    FirstName: FirstNameTypeQuery.GQLQuery(),
    FatherName: FirstNameTypeQuery.GQLQuery(),
    InfoNotes: InfoNoteTypeQuery.GQLQuery(),
    OccupationActivity: OccupationActivityTypeQuery.GQLQuery(),
    ContactDetails: ContactDetailTypeQuery.GQLQuery({
        Address: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        })
    }),
    // Courier: CourierTypeQuery.GQLQuery(),
    FinancialDetails: FinancialDetailTypeQuery.GQLQuery(),
    TraderProfitMargin: TraderProfitMarginTypeQuery.GQLQuery(),
    CTAXOffice: TAXOfficeTypeQuery.GQLQuery(),
    Subjects: CustomersToEntitiesSubjectTypeQuery.GQLQuery({
        Subject: EntitySubjectInfoTypeQuery.GQLQuery()
    })
});

const customersQueryForBatch = CustomerTypeQuery.GQLQuery({
    ...CustomerTypeQuery.GQLQuery(),
    Addresses: AddressTypeQuery.GQLQuery({
        City: CityTypeQuery.GQLQuery()
    }),
    FirstName: FirstNameTypeQuery.GQLQuery(),
    FatherName: FirstNameTypeQuery.GQLQuery(),
    InfoNotes: InfoNoteTypeQuery.GQLQuery(),
    OccupationActivity: OccupationActivityTypeQuery.GQLQuery(),
    ContactDetails: ContactDetailTypeQuery.GQLQuery({
        Address: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        })
    }),
    // Courier: CourierTypeQuery.GQLQuery(),
    FinancialDetails: FinancialDetailTypeQuery.GQLQuery(),
    TraderProfitMargin: TraderProfitMarginTypeQuery.GQLQuery(),
    CTAXOffice: TAXOfficeTypeQuery.GQLQuery()
});
